/* Repository path: frontend/src/features/plans/SavedPlanPage.tsx */


import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import { getPlan, requestDeletion, saveItemActual, saveItemDeadline, saveItemReminder, setReminder } from "../../api/planner";
import { isValidEstimatePayload, itemReminderStatusIfValid, planningBlockIfValid, remindersBlockIfValid } from "../../api/estimate";
import { Button } from "../../components/Button";
import { Confirmation } from "../../components/Confirmation";
import { ErrorSummary } from "../../components/ErrorSummary";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { ItemReminderSection } from "../../components/ItemReminder";
import { LoadingState } from "../../components/LoadingState";
import { RecordActual } from "../../components/RecordActual";
import { StatusChip } from "../../components/StatusChip";
import { SupportPrompt } from "../../components/SupportPrompt";
import type { PlanItem, ProfileEditResult, SavedPlan } from "../../types";
import { formatDate, formatReminderWhen } from "../../utils/format";
import { EstimateView } from "../estimates/EstimateView";
import { ProfileChangeSummary } from "../estimates/ProfileChangeSummary";
import { ItemDetailNotFound, PlanItemDetail } from "../estimates/PlanItemDetail";

interface Props {
  publicId: string;
  online: boolean;
  announce: (message: string) => void;
  navigate: (path: string) => void;
  onImprove?: (item?: PlanItem) => void;
  /** W4-07: opens the profile editor at `/plans/{publicId}/edit-profile`. */
  onEdit?: () => void;
  /** W4-05: present only when the URL fragment names an item. See
   * `../../app/router.ts` and
   * docs/architecture/adr/ADR-0009-client-route-privacy-and-item-detail-addressing.md. */
  detailItemCode?: string;
  /** The item code whose detail was just closed, if any. See `useRouter`. */
  focusItemCode?: string;
  onFocusHandled?: () => void;
  /** W4-07: the just-completed profile edit's "what changed" summary, if
   * any — see `ResultPage`'s identical props for the preview path. */
  changeSummary?: ProfileEditResult | null;
  onChangeSummaryHandled?: () => void;
}

export function SavedPlanPage({
  publicId,
  online,
  announce,
  navigate,
  onImprove,
  onEdit,
  detailItemCode,
  focusItemCode,
  onFocusHandled,
  changeSummary = null,
  onChangeSummaryHandled,
}: Props) {
  const [plan, setPlan] = useState<SavedPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadFailure, setLoadFailure] = useState<"not_found" | "transient" | null>(null);

  const loadPlan = useCallback(() => {
    let active = true;
    setLoading(true);
    setErrors({});
    setLoadFailure(null);
    getPlan(publicId)
      .then((data) => { if (active) setPlan(data); })
      .catch((error) => {
        if (!active) return;
        const notFound = error instanceof ApiError && error.status === 404;
        setLoadFailure(notFound ? "not_found" : "transient");
        setErrors({
          page: notFound
            ? "We couldn’t find a saved plan for this link."
            : "We couldn’t load your saved plan right now. Try again.",
        });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [publicId]);

  useEffect(() => {
    let active = true;
    getPlan(publicId)
      .then((data) => { if (active) setPlan(data); })
      .catch((error) => {
        if (!active) return;
        const notFound = error instanceof ApiError && error.status === 404;
        setLoadFailure(notFound ? "not_found" : "transient");
        setErrors({
          page: notFound
            ? "We couldn’t find a saved plan for this link."
            : "We couldn’t load your saved plan right now. Try again.",
        });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [publicId]);

  const toggleReminder = async () => {
    if (!plan || !online || (!plan.reminder.enabled && !plan.reminder.can_enable)) return;
    const enabled = !plan.reminder.enabled;
    setBusy(true); setErrors({});
    try {
      await setReminder(publicId, enabled);
      setPlan(await getPlan(publicId));
      announce(enabled ? "Reminder on" : "Reminder off");
    } catch (error) {
      setErrors({ page: error instanceof ApiError ? error.message : "We couldn’t change the reminder. Try again." });
    } finally { setBusy(false); }
  };

  const deletePlan = async () => {
    if (!online) return;
    setBusy(true); setErrors({});
    try {
      await requestDeletion(publicId);
      setDeleted(true); announce("Deletion requested");
    } catch (error) {
      setErrors({ page: error instanceof ApiError ? error.message : "We couldn’t request deletion. Try again." });
    } finally { setBusy(false); }
  };

  // W4-04: deliberately not caught here. `TimingLine` catches its own
  // errors and shows them at the field, next to the control that failed —
  // never in the shared page-level `ErrorSummary` above. See
  // docs/design/features/W4-04.md, "The control".
  const saveDeadline = async (itemCode: string, deadline: string) => {
    const updated = await saveItemDeadline(publicId, itemCode, deadline);
    setPlan(updated);
    announce(`Saved. We’ll use ${formatDate(deadline)} for this one.`);
  };

  const removeDeadline = async (itemCode: string) => {
    const updated = await saveItemDeadline(publicId, itemCode, null);
    setPlan(updated);
    announce("Removed. We’re back to our estimate for this one.");
  };

  // W4-06: deliberately not caught here either — `RecordActual` catches
  // its own errors and shows them at the field, the same discipline
  // `saveDeadline`/`removeDeadline` already follow for `TimingLine`.
  const saveActual = async (itemCode: string, state: string, actualAmount: string | null) => {
    const updated = await saveItemActual(publicId, itemCode, state, actualAmount);
    setPlan(updated);
    announce("Saved. We’ll use this for what’s ahead.");
  };

  const undoActual = async (itemCode: string) => {
    const updated = await saveItemActual(publicId, itemCode, "open", null);
    setPlan(updated);
    announce("Removed. We’re back to our estimate for this one.");
  };

  // W5-02: deliberately not caught here either — `ItemReminderLine`/
  // `ItemReminderSection` catch their own errors and show them at the
  // control that failed, the same discipline `saveDeadline`/
  // `removeDeadline`/`saveActual`/`undoActual` above already follow. No
  // optimistic update: the reminder UI changes only once `setPlan` below
  // replaces state with the server's own response. See
  // docs/design/features/W5-02.md, acceptance criterion 5.
  const setItemReminder = async (itemCode: string, enabled: boolean) => {
    const updated = await saveItemReminder(publicId, itemCode, enabled);
    setPlan(updated);
    const englishName = updated.items.find((row) => row.code === itemCode)?.english_name ?? "this item";
    announce(
      enabled
        ? `Reminder on for ${englishName}.`
        : `Reminder off for ${englishName}. Your other reminders are unchanged.`,
    );
  };

  if (loading) return <LoadingState label="Loading your saved plan" />;
  if (deleted) return (
    <section className="confirmation confirmation--success page--narrow" role="status">
      <p className="eyebrow">Deletion requested</p>
      <h1>Your saved plan access has ended</h1>
      <p>The saved profile, plan contact, and future reminder are being removed. This cannot be undone.</p>
      <Button onClick={() => navigate("/")}>Return to start</Button>
    </section>
  );
  if (!plan) return (
    <section className="empty-state page--narrow">
      <ErrorSummary errors={errors} />
      {loadFailure === "transient" ? (
        <>
          <h1>We couldn’t load your saved plan yet</h1>
          <p>This may be a temporary connection or server problem. Your link has not been classified as invalid.</p>
          <Button onClick={loadPlan} disabled={!online}>Try loading the plan again</Button>
        </>
      ) : (
        <>
          <h1>We couldn’t find a saved plan for this link</h1>
          <p>The link may be invalid, expired, or no longer authorized. This response does not disclose whether a plan exists.</p>
          <Button onClick={() => navigate("/access/recovery")}>Request a new secure link</Button>
        </>
      )}
    </section>
  );

  const backHref = `/plans/${encodeURIComponent(publicId)}`;
  // W5-02: computed once, reused by every panel line below rather than
  // re-validating the block on each read — the same "compute once, thread
  // through" discipline `itemsByCode` follows in `EstimateView`.
  const remindersBlock = remindersBlockIfValid(plan);
  const nextItemReminder = remindersBlock?.item_level.next ?? null;

  // W4-05: guarded the same way `EstimateView` guards itself — a
  // structurally broken plan always falls through to the normal render
  // below (which shows the existing boundary card), never into the
  // detail branch. See `ResultPage` for the identical rationale.
  if (detailItemCode && isValidEstimatePayload(plan)) {
    const item = plan.items.find((row) => row.code === detailItemCode);
    if (!item) {
      return (
        <div className="result-page">
          <ItemDetailNotFound backHref={backHref} />
        </div>
      );
    }
    return (
      <div className="result-page">
        <PlanItemDetail
          key={item.code}
          item={item}
          planning={planningBlockIfValid(plan)}
          editableTiming
          onSaveDeadline={saveDeadline}
          onRemoveDeadline={removeDeadline}
          onImprove={onImprove ?? (() => navigate(`/plans/${encodeURIComponent(publicId)}/refine`))}
          backHref={backHref}
          recordActual={<RecordActual item={item} onSave={saveActual} onUndo={undoActual} />}
          reminder={
            itemReminderStatusIfValid(item.reminder_status)
              ? <ItemReminderSection item={item} onSetReminder={setItemReminder} online={online} />
              : undefined
          }
          feedback={
            <FeedbackPanel
              surface="item_detail"
              accessContext="saved"
              item={item}
              online={online}
              announce={announce}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className="result-page">
      <ErrorSummary errors={errors} />
      <EstimateView
        estimate={plan}
        saved
        onEdit={onEdit}
        onImprove={onImprove ?? (() => navigate(`/plans/${encodeURIComponent(publicId)}/refine`))}
        onSaveDeadline={saveDeadline}
        onRemoveDeadline={removeDeadline}
        onSetItemReminder={setItemReminder}
        online={online}
        focusItemCode={focusItemCode}
        onFocusHandled={onFocusHandled}
        changeSummary={
          changeSummary && (
            <ProfileChangeSummary
              result={changeSummary}
              items={plan.items}
              onImprove={onImprove ? () => onImprove() : () => navigate(`/plans/${encodeURIComponent(publicId)}/refine`)}
              onDismiss={onChangeSummaryHandled}
            />
          )
        }
        feedback={
          <FeedbackPanel
            surface="saved_plan"
            accessContext="saved"
            online={online}
            announce={announce}
          />
        }
      />
      {/*
        W4-09: one quiet, dismissible block, deliberately placed above
        "Manage this saved plan" — a request for money must never sit next
        to a destructive action. Renders nothing until the server resolves
        an enabled, allowlisted link, and nothing forever if it never does.
        See docs/architecture/features/W4-09.md and
        docs/architecture/adr/ADR-0013-external-support-link-boundary.md.
      */}
      <SupportPrompt placement="saved_plan" announce={announce} />
      <section className="management" aria-labelledby="manage-plan-title">
        <div>
          <p className="eyebrow">Your choices</p>
          <h2 id="manage-plan-title" tabIndex={-1}>Manage this saved plan</h2>
          <p>A reminder is only active after explicit opt-in and email verification.</p>
          {remindersBlock && (
            <p>
              Reminders for this plan are a monthly check-in plus an email before dates in your
              plan. You can turn off reminders for single items on each item.
            </p>
          )}
        </div>
        <div className="management__status">
          <StatusChip tone={plan.reminder.enabled ? "success" : "neutral"}>Reminder: {plan.reminder.enabled ? "On" : "Off"}</StatusChip>
          {plan.reminder.enabled && plan.reminder.last_outcome === "failed" && (
            <><StatusChip tone="error">Reminder could not be delivered yet</StatusChip><p>We’ll keep the plan available. You can leave reminders on for the next scheduled attempt or turn them off.</p></>
          )}
          {plan.reminder.enabled && plan.reminder.last_outcome === "unknown" && (
            <><StatusChip tone="warning">Reminder delivery is not confirmed</StatusChip><p>We will not send a blind duplicate while delivery is being reconciled.</p></>
          )}
          {plan.reminder.enabled && plan.reminder.next_due_at && <p>Next scheduled reminder: <strong>{formatDate(plan.reminder.next_due_at)}</strong></p>}
          {plan.reminder.enabled && remindersBlock?.item_level.enabled && nextItemReminder && (
            <p>
              Next reminder about an item: <strong>
                {plan.items.find((row) => row.code === nextItemReminder.item_code)?.english_name
                  ?? nextItemReminder.item_code}
              </strong>, {formatReminderWhen(nextItemReminder.due_on, nextItemReminder.precision)}.
            </p>
          )}
          {(plan.reminder.enabled || plan.reminder.can_enable) && (
            <Button variant="secondary" onClick={toggleReminder} busy={busy} disabled={!online}>
              {busy ? "Saving…" : plan.reminder.enabled ? "Turn reminders off" : "Turn reminders on"}
            </Button>
          )}
          {plan.reminder.enabled && (
            <p className="field-hint">
              Turning reminders off stops every reminder email for this plan, including reminders
              about single items.
            </p>
          )}
          {!plan.reminder.enabled && !plan.reminder.can_enable && <p>Reminders cannot be enabled because this plan does not have reminder consent. You can still review or delete the plan.</p>}
          {!online && <p className="field-hint" role="status">Connect to the internet to change reminders or request deletion.</p>}
        </div>
        <div className="danger-zone">
          <h3>Delete this plan</h3>
          <p>Request removal of the saved profile, contact, plan access, and future reminder.</p>
          {!confirmDelete && <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={!online}>Request deletion</Button>}
          {confirmDelete && (
            <Confirmation title="Delete your saved plan?" confirmLabel="Yes, request deletion" onConfirm={deletePlan} onCancel={() => setConfirmDelete(false)} busy={busy} confirmDisabled={!online}>
              <p>This removes your saved profile, estimate access, email contact, and future reminders. It cannot be undone.</p>
              {!online && <p className="field-hint">Reconnect before confirming deletion.</p>}
            </Confirmation>
          )}
        </div>
      </section>
    </div>
  );
}


